import axios from 'axios';
import { getAxiosConfig, getQueryString } from '../shared';

export const UNLAYER_EMAIL_FUNNEL_BASE_URL = '/app/unlayerEmailFunnel';

export const getAllUnlayerEmailFunnelGlobalTemplates = async () => {
    try {
        const { data } = await axios.get(
            `${UNLAYER_EMAIL_FUNNEL_BASE_URL}/globalTemplates`,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get global templates');
    }
};

export const getUnlayerEmailFunnelGlobalTemplate = async ({ id: templateId }) => {
    try {
        const { data } = await axios.get(
            encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/globalTemplate/${templateId}`),
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get global templates');
    }
};

export const getAllUnlayerEmailFunnelSavedTemplates = async (page) => {
    const queryString = getQueryString({ page });

    try {
        const data = await axios.get(
            encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/savedTemplates/${queryString}`),
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get saved templates');
    }
};

export const getUnlayerEmailFunnelSavedTemplate = async ({ id: templateId }) => {
    try {
        if (!templateId) throw new Error();

        const { data } = await axios.get(
            `${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${templateId}/saved`,
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not get global template');
    }
};

export const updateUnlayerEmailFunnelSavedTemplate = async ({ marketingEmailId, template }) => {
    try {
        const { id: externalId } = template;

        if (!externalId || !marketingEmailId) throw new Error();

        const res = await axios.put(
            `${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${marketingEmailId}/saved`,
            { externalId },
        );

        return res.data;
    } catch (error) {
        throw new Error('Could not update saved templates');
    }
};

export const deleteUnlayerEmailFunnelSavedTemplate = async ({ id: templateId }) => {
    try {
        if (!templateId) throw new Error();

        await axios.delete(
            encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/deleteSavedTemplate/${templateId}`),
            getAxiosConfig(),
        );
    } catch (error) {
        throw new Error('Could not delete saved template');
    }
};

export const updateUnlayerEmailFunnelPublicTemplate = async ({ marketingEmailId, template }) => {
    try {
        const { id: externalId } = template;

        if (!externalId || !marketingEmailId) throw new Error();

        const { data } = await axios.put(
            encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${marketingEmailId}/public`),
            { externalId },
            getAxiosConfig(),
        );

        return data;
    } catch (error) {
        throw new Error('Could not update public templates');
    }
};

export const renameSavedEmailFunnelTemplate = async({ template, name: title }) => {
    try {
        const { id: templateId } = template;

        if (!templateId || !title) throw new Error();

        await axios.put(
            encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/renameSavedTemplate/${templateId}`),
            { title },
        );
    } catch (error) {
        throw new Error('Could not update template');
    }
};

export const sendTestEmailMultiple = (params) => {
    return axios.post(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/sendTestEmailMultiple`, params);
};

export const getMergeFields = async (funnelId = 0) => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/mergeFields/${funnelId}`);

        return data.categories;
    } catch (error) {
        throw new Error(`Could not get merge fields - Message: ${error.message}`);
    }
};

export const getCampaignLinks = async (funnelId) => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/campaignLinks/${funnelId}`);

        return data;
    } catch (error) {
        throw new Error(`ould not get campaign links - Message: ${error.message}`);
    }
};

export const getUsers = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/users`);

        return data;
    } catch (error) {
        throw new Error(`Could not get users - Message: ${error.message}`);
    }
};

export const getUserSignatures = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/userSignatures`);

        return data;
    } catch (error) {
        throw new Error(`Could not get user signatures - Message: ${error.message}`);
    }
};

export const getAppointmentTypes = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/appointmentTypes`);

        return data ?? [];
    } catch (error) {
        throw new Error(`Could not get appointment types - Message: ${error.message}`);
    }
};

export const getTags = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/tags`);

        return data;
    } catch (error) {
        throw new Error(`Could not get tags - Message: ${error.message}`);
    }
};

export const getCurrentUser = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/user`);

        return data;
    } catch (error) {
        throw new Error(`Could not get current user - Message: ${error.message}`);
    }
};

export const getFunnelPreviewHtml = async ({ templateId, funnelId }) => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${templateId}/funnel/${funnelId}/preview`);

        return data;
    } catch (error) {
        throw new Error(`Could not get funnel preview html - Message: ${error.message}`);
    }
};

export const getBardTemplates = (pageToken, pageSize) => {
    const params = {
        filter_criteria: 'ALL',
        page_size: pageSize,
    };

    if (pageToken) {
        params.next_page = pageToken;
    }

    const queryString = getQueryString({
        ...params,
    });

    return axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${queryString}`);
};

export const getFunnelEmail = async ({
    marketingEmailId,
    funnelId,
}) => {
    try {
        const { data } = await axios.get(`/app/unlayerEmailFunnel/template/${marketingEmailId}?funnelId=${funnelId}`);

        return data.unlayerEmailTemplate;
    } catch (error) {
        throw new Error(`Could not get funnel email - Message: ${error}`);
    }
};

export const saveFunnelEmail = ({
    id: marketingEmailId,
    design,
    htmlBody,
    trackedLinks,
    subject,
    previewText,
    toAddress,
    fromAddress,
    replyToAddress,
}, funnelId) => {
    return axios.put(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${marketingEmailId}`, {
        design: JSON.stringify(design),
        html: htmlBody,
        trackedLinks,
        subject,
        previewText,
        toAddress,
        fromAddress,
        replyToAddress,
        funnelId,
    });
};

export const updateBardTemplateMigrationStatus = async ({ templateId, migrationStatus }) => {
    try {
        const { data } = await axios.patch(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/bardTemplate/${templateId}`, {
            migrationStatus,
        });

        return data;
    } catch (error) {
        throw new Error(`Could not update template migration status - Message: ${error}`);
    }
};

export const updateBardPrivateTemplateMigrationStatus = async ({ templateId, migrationStatus }) => {
    try {
        const { data } = await axios.patch(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/bardPrivateTemplate/${templateId}`, {
            migrationStatus,
        });

        return data;
    } catch (error) {
        throw new Error(`Could not update private template migration status - Message: ${error}`);
    }
};

export const getBrandLogos = async () => {
    const params = {
        status: 'OK',
    };

    const queryString = getQueryString({
        ...params,
    });

    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/branding/logos?${queryString}`);

        return data;
    } catch (error) {
        throw new Error(`Could not get brand logos - Message: ${error}`);
    }
};

export const getBrandPalette = async () => {
    try {
        const { data } = await axios.get(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/branding/palette`);

        return data;
    } catch (error) {
        throw new Error(`Could not get brand palette - Message: ${error}`);
    }
};

export const getFunnelMergeFields = async (funnelId) => {
    try {
        const { data: { funnelMergeFields } } = await axios.get(`/app/unlayerEmailFunnel/funnelMergeFields/${funnelId}`);

        return funnelMergeFields;
    } catch (error) {
        throw new Error(`Could not get funnel merge fields - Message: ${error}`);
    }
};

export const getUnlayerUserUploadedFiles = async (pageKey) => {
    const { data } = await axios.get('/app/unlayerEmailFunnel/files', {
        params: {
            pageKey,
        },
    });

    return data;
};

export const getTemplateDependencyList = async (templateId) => {
    const {data} = await axios.get( encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${templateId}/dependencies?filter=dependencyType==LOGO`));

    return data;
}

export const createLogoDependency = async (templateId, logoId) => {
    const { data } = await axios.post( encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${templateId}/dependencies`), {
        dependencyId: logoId,
        dependencyType: 'LOGO',
        label: ''
    });

    return data;
}

export const deleteLogoDependency = async (templateId, dependencyKey) => {
    const { data } = await axios.delete( encodeURI(`${UNLAYER_EMAIL_FUNNEL_BASE_URL}/template/${templateId}/dependencies/${dependencyKey}`));

    return data;
}